import React from 'react';
import { useFoodTemperature } from '../context/FoodTemperatureContext';
import { IoCheckmarkCircleOutline, IoWarningOutline, IoTimeOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { LuClipboardList } from 'react-icons/lu';

export default function TemperatureMetricsDashboard() {
  const { stats, formattedDate } = useFoodTemperature();
  const percentage = count => stats.total ? Math.round(count / stats.total * 100) : 0;
  const cards = [
    {
      id: 1,
      title: 'Entries Logged',
      value: stats.total,
      subtext: formattedDate,
      icon: <LuClipboardList className="text-xl text-blue-600" />,
      iconBg: 'bg-blue-50',
      hasProgressBar: false,
      progressPercent: 0,
    },
    {
      id: 2,
      title: 'Within Safe Range',
      value: stats.withinRange,
      subtext: `${percentage(stats.withinRange)}%`,
      icon: <IoCheckmarkCircleOutline className="text-xl text-emerald-600" />,
      iconBg: 'bg-emerald-50',
      hasProgressBar: false,
    },
    {
      id: 3,
      title: 'Out of Range',
      value: stats.outOfRange,
      subtext: `${percentage(stats.outOfRange)}%`,
      icon: <IoWarningOutline className="text-xl text-rose-500" />,
      iconBg: 'bg-rose-50',
      hasProgressBar: false,
    },
    {
      id: 4,
      title: 'Status Not Recorded',
      value: stats.unassessed,
      subtext: `${percentage(stats.unassessed)}%`,
      icon: <IoTimeOutline className="text-xl text-amber-500" />,
      iconBg: 'bg-amber-50',
      hasProgressBar: false,
    },
  ];

  return (
    <div className="w-full  p-4 flex flex-col gap-4 bg-gray-50/50">
      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div 
            key={card.id} 
            className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col justify-between min-h-[140px]"
          >
            <div className="flex gap-4">
              {/* Icon Container */}
              <div className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${card.iconBg}`}>
                {card.icon}
              </div>

              {/* Text Info */}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-gray-500 tracking-wide">
                  {card.title}
                </span>
                <span className="text-2xl font-bold text-gray-900 leading-tight">
                  {card.value}
                </span>
                <span className="text-xs text-gray-400 font-medium">
                  {card.subtext}
                </span>
              </div>
            </div>

            {/* Optional Progress Bar */}
            {card.hasProgressBar && (
              <div className="w-full bg-gray-100 h-2 rounded-full mt-4 overflow-hidden">
                <div 
                  className="bg-emerald-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${card.progressPercent}%` }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Alert Box */}
      <div className="flex items-start gap-3 bg-blue-50/60 border border-blue-100 rounded-xl p-4">
        <IoInformationCircleOutline className="text-blue-600 text-lg shrink-0 mt-0.5" />
        <p className="text-sm font-medium text-blue-700 leading-relaxed">
          Record temperatures for all hot and cold holding items. Take action if temperatures are out of the safe range.
        </p>
      </div>
    </div>
  );
}
